import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { formatDateTime } from '../../utils/dateFormat';

interface ShareRecord {
  share_id: number;
  file_id: number;
  file_name: string;
  shared_by_name: string;
  shared_with_email: string;
  permission: string;
  status: string;
  created_at: string | null;
  verified_at: string | null;
  last_access: string | null;
  expires_at: string | null;
  session_count: number;
  anomaly_count: number;
}

interface ActivitySummary {
  active_count: number;
  pending_count: number;
  revoked_count: number;
  expired_count: number;
  external_count: number;
  alert_count: number;
  pending_approval_count: number;
}

interface PendingShare {
  share_id: number;
  file_id: number;
  file_name: string;
  shared_by_name: string;
  shared_with_email: string;
  permission: string;
  created_at: string | null;
  molecule_count: number;
}

interface SharingPolicy {
  tenant_name: string;
  allow_external_sharing: boolean;
  allowed_share_domains: string[];
  require_otp: boolean;
  default_expiry_days: number | null;
  watermarking_enabled: boolean;
  require_external_approval: boolean;
}

const STATUS_BADGES: Record<string, { bg: string; text: string; label: string }> = {
  active: { bg: 'bg-green-100', text: 'text-green-700', label: 'Active' },
  pending: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Pending' },
  pending_approval: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Awaiting Approval' },
  rejected: { bg: 'bg-red-100', text: 'text-red-700', label: 'Rejected' },
  revoked: { bg: 'bg-red-100', text: 'text-red-700', label: 'Revoked' },
  expired: { bg: 'bg-gray-100', text: 'text-gray-500', label: 'Expired' },
};

const EXPIRY_OPTIONS = [
  { value: 0, label: 'Never' },
  { value: 7, label: '7 days' },
  { value: 14, label: '14 days' },
  { value: 30, label: '30 days' },
  { value: 60, label: '60 days' },
  { value: 90, label: '90 days' },
];

const SharingActivityPage: React.FC = () => {
  const { token } = useAuth();
  const [shares, setShares] = useState<ShareRecord[]>([]);
  const [summary, setSummary] = useState<ActivitySummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [revokeLoading, setRevokeLoading] = useState<number | null>(null);

  // Sharing Policy state
  const [policy, setPolicy] = useState<SharingPolicy | null>(null);
  const [policyLoading, setPolicyLoading] = useState(true);
  const [policySaving, setPolicySaving] = useState(false);
  const [policyMsg, setPolicyMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [policyForm, setPolicyForm] = useState({
    allow_external_sharing: true,
    allowed_share_domains: '',
    require_otp: true,
    default_expiry_days: 0,
    watermarking_enabled: true,
    require_external_approval: true,
  });

  // Pending approval state
  const [pendingShares, setPendingShares] = useState<PendingShare[]>([]);
  const [approveLoading, setApproveLoading] = useState<number | null>(null);
  const [rejectLoading, setRejectLoading] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState<string>('');
  const [rejectingShareId, setRejectingShareId] = useState<number | null>(null);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Fetch sharing policy
  useEffect(() => {
    fetch('/api/v1/admin/tenant-settings/sharing', { headers })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        if (data) {
          setPolicy(data);
          setPolicyForm({
            allow_external_sharing: data.allow_external_sharing,
            allowed_share_domains: (data.allowed_share_domains || []).join(', '),
            require_otp: data.require_otp,
            default_expiry_days: data.default_expiry_days || 0,
            watermarking_enabled: data.watermarking_enabled,
            require_external_approval: data.require_external_approval ?? true,
          });
        }
        setPolicyLoading(false);
      })
      .catch(() => setPolicyLoading(false));
  }, [token]);

  const handleSavePolicy = async () => {
    setPolicySaving(true);
    setPolicyMsg(null);
    try {
      const domains = policyForm.allowed_share_domains
        .split(',')
        .map((d) => d.trim().toLowerCase())
        .filter(Boolean);

      const res = await fetch('/api/v1/admin/tenant-settings/sharing', {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          allow_external_sharing: policyForm.allow_external_sharing,
          allowed_share_domains: domains,
          require_otp: policyForm.require_otp,
          default_expiry_days: policyForm.default_expiry_days,
          watermarking_enabled: policyForm.watermarking_enabled,
          require_external_approval: policyForm.require_external_approval,
        }),
      });

      if (res.ok) {
        const updated = await res.json();
        setPolicy(updated);
        setPolicyMsg({ type: 'success', text: 'Sharing policy saved successfully' });
        setTimeout(() => setPolicyMsg(null), 4000);
      } else {
        const err = await res.json();
        setPolicyMsg({ type: 'error', text: err.detail || 'Failed to save policy' });
      }
    } catch {
      setPolicyMsg({ type: 'error', text: 'Network error' });
    }
    setPolicySaving(false);
  };

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'all') params.set('status_filter', statusFilter);
      if (search.trim()) params.set('search', search.trim());

      const [sharesRes, summaryRes] = await Promise.all([
        fetch(`/api/v1/admin/sharing-activity?${params}`, { headers }),
        fetch('/api/v1/admin/sharing-activity/summary', { headers }),
      ]);

      if (sharesRes.ok) {
        const data = await sharesRes.json();
        setShares(data.shares || []);
      }
      if (summaryRes.ok) {
        setSummary(await summaryRes.json());
      }
    } catch (err) {
      console.error('Failed to fetch sharing activity:', err);
    }
    setLoading(false);
  }, [statusFilter, search, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRevoke = async (fileId: number, shareId: number) => {
    setRevokeLoading(shareId);
    try {
      const res = await fetch(`/api/v1/files/${fileId}/share/${shareId}`, {
        method: 'DELETE',
        headers,
      });
      if (res.ok) {
        await fetchData();
      }
    } catch (err) {
      console.error('Failed to revoke share:', err);
    }
    setRevokeLoading(null);
  };

  // Fetch pending approval shares
  const fetchPending = useCallback(async () => {
    try {
      const res = await fetch('/api/v1/admin/shares/pending', { headers });
      if (res.ok) {
        const data = await res.json();
        setPendingShares(data.pending_shares || []);
      }
    } catch (err) {
      console.error('Failed to fetch pending shares:', err);
    }
  }, [token]);

  useEffect(() => {
    fetchPending();
  }, [fetchPending]);

  const handleApprove = async (shareId: number) => {
    setApproveLoading(shareId);
    try {
      const res = await fetch(`/api/v1/admin/shares/${shareId}/approve`, {
        method: 'POST',
        headers,
      });
      if (res.ok) {
        await Promise.all([fetchPending(), fetchData()]);
      }
    } catch (err) {
      console.error('Failed to approve share:', err);
    }
    setApproveLoading(null);
  };

  const handleReject = async (shareId: number) => {
    setRejectLoading(shareId);
    try {
      const res = await fetch(`/api/v1/admin/shares/${shareId}/reject`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ reason: rejectReason || null }),
      });
      if (res.ok) {
        setRejectingShareId(null);
        setRejectReason('');
        await Promise.all([fetchPending(), fetchData()]);
      }
    } catch (err) {
      console.error('Failed to reject share:', err);
    }
    setRejectLoading(null);
  };

  return (
    <div className="space-y-6">
      {/* Sharing Policy Settings */}
      {!policyLoading && policy && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-teal-50 flex items-center justify-center">
                <svg className="w-4 h-4 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-800">Sharing Policy</h3>
                <p className="text-xs text-gray-400">{policy.tenant_name}</p>
              </div>
            </div>
            {policyMsg && (
              <span className={`text-xs font-medium ${policyMsg.type === 'success' ? 'text-teal-600' : 'text-red-600'}`}>
                {policyMsg.text}
              </span>
            )}
          </div>

          <div className="px-5 py-4 space-y-4">
            {/* Row 1: External Sharing + Allowed Domains */}
            <div className="flex items-start gap-6">
              <label className="flex items-center gap-3 min-w-[200px] cursor-pointer">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={policyForm.allow_external_sharing}
                    onChange={(e) => setPolicyForm({ ...policyForm, allow_external_sharing: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-gray-200 rounded-full peer-checked:bg-teal-500 transition-colors" />
                  <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform peer-checked:translate-x-4" />
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-700">External Sharing</span>
                  <p className="text-xs text-gray-400">Allow sharing with people outside your organisation</p>
                </div>
              </label>

              {policyForm.allow_external_sharing && (
                <>
                  <label className="flex items-center gap-3 min-w-[200px] cursor-pointer">
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={policyForm.require_external_approval}
                        onChange={(e) => setPolicyForm({ ...policyForm, require_external_approval: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-9 h-5 bg-gray-200 rounded-full peer-checked:bg-orange-500 transition-colors" />
                      <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform peer-checked:translate-x-4" />
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-700">Admin Approval</span>
                      <p className="text-xs text-gray-400">Require admin approval for external shares</p>
                    </div>
                  </label>

                  <div className="flex-1">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Allowed Domains</label>
                    <input
                      type="text"
                      value={policyForm.allowed_share_domains}
                      onChange={(e) => setPolicyForm({ ...policyForm, allowed_share_domains: e.target.value })}
                      placeholder="Leave blank for any domain, or: pharma.com, partner.co"
                      className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-teal-500"
                    />
                    <p className="text-xs text-gray-400 mt-1">Comma-separated. When set, files can only be shared with these domains.</p>
                  </div>
                </>
              )}
            </div>

            {/* Row 2: OTP + Watermarking + Default Expiry */}
            <div className="flex items-start gap-6">
              <label className="flex items-center gap-3 min-w-[200px] cursor-pointer">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={policyForm.require_otp}
                    onChange={(e) => setPolicyForm({ ...policyForm, require_otp: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-gray-200 rounded-full peer-checked:bg-teal-500 transition-colors" />
                  <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform peer-checked:translate-x-4" />
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-700">OTP Verification</span>
                  <p className="text-xs text-gray-400">Require email code verification for shared links</p>
                </div>
              </label>

              <label className="flex items-center gap-3 min-w-[200px] cursor-pointer">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={policyForm.watermarking_enabled}
                    onChange={(e) => setPolicyForm({ ...policyForm, watermarking_enabled: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-gray-200 rounded-full peer-checked:bg-teal-500 transition-colors" />
                  <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform peer-checked:translate-x-4" />
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-700">Watermarking</span>
                  <p className="text-xs text-gray-400">Stamp guest/trial views with email and timestamp</p>
                </div>
              </label>

              <div className="min-w-[180px]">
                <label className="block text-xs font-medium text-gray-500 mb-1">Default Share Expiry</label>
                <select
                  value={policyForm.default_expiry_days}
                  onChange={(e) => setPolicyForm({ ...policyForm, default_expiry_days: parseInt(e.target.value) })}
                  className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-teal-500"
                >
                  {EXPIRY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-400 mt-1">
                  {policyForm.default_expiry_days > 0
                    ? `Members cannot set expiry longer than ${policyForm.default_expiry_days} days`
                    : 'Members can set any expiry or none'}
                </p>
              </div>
            </div>
          </div>

          <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
            <p className="text-xs text-gray-400">
              Policy changes are logged in the audit trail
            </p>
            <button
              onClick={handleSavePolicy}
              disabled={policySaving}
              className="px-5 py-2 text-sm font-medium bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 transition-colors"
            >
              {policySaving ? 'Saving...' : 'Save Policy'}
            </button>
          </div>
        </div>
      )}

      {/* Pending Approval Section */}
      {pendingShares.length > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-orange-100 flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center">
              <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-orange-800">
                Pending Approval ({pendingShares.length})
              </h3>
              <p className="text-xs text-orange-600">External shares awaiting your review</p>
            </div>
          </div>
          <div className="divide-y divide-orange-100">
            {pendingShares.map((ps) => (
              <div key={ps.share_id} className="px-5 py-3 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{ps.file_name}</p>
                  <p className="text-xs text-gray-500">
                    {ps.shared_by_name} wants to share with <span className="font-medium">{ps.shared_with_email}</span>
                    {' '}&middot;{' '}{ps.permission} access
                    {ps.molecule_count > 0 && <> &middot; {ps.molecule_count.toLocaleString()} compounds</>}
                  </p>
                  {ps.created_at && (
                    <p className="text-xs text-gray-400 mt-0.5">{formatDateTime(ps.created_at)}</p>
                  )}
                </div>

                {rejectingShareId === ps.share_id ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      placeholder="Reason (optional)"
                      className="w-48 px-2 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:border-red-400"
                    />
                    <button
                      onClick={() => handleReject(ps.share_id)}
                      disabled={rejectLoading === ps.share_id}
                      className="px-3 py-1.5 text-xs font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                    >
                      {rejectLoading === ps.share_id ? 'Rejecting...' : 'Confirm Reject'}
                    </button>
                    <button
                      onClick={() => { setRejectingShareId(null); setRejectReason(''); }}
                      className="px-2 py-1.5 text-xs text-gray-500 hover:text-gray-700"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleApprove(ps.share_id)}
                      disabled={approveLoading === ps.share_id}
                      className="px-4 py-1.5 text-xs font-medium bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 transition-colors"
                    >
                      {approveLoading === ps.share_id ? 'Approving...' : 'Approve'}
                    </button>
                    <button
                      onClick={() => setRejectingShareId(ps.share_id)}
                      className="px-4 py-1.5 text-xs font-medium bg-white text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
                    >
                      Reject
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-5 gap-4">
          <div className="bg-green-50 border border-green-100 rounded-xl p-4">
            <p className="text-xs text-green-600 font-medium uppercase">Active Shares</p>
            <p className="text-2xl font-bold text-green-700 mt-1">{summary.active_count}</p>
          </div>
          <div className="bg-amber-50 border border-amber-100 rounded-xl p-4">
            <p className="text-xs text-amber-600 font-medium uppercase">Pending</p>
            <p className="text-2xl font-bold text-amber-700 mt-1">{summary.pending_count}</p>
          </div>
          <div className="bg-orange-50 border border-orange-100 rounded-xl p-4">
            <p className="text-xs text-orange-600 font-medium uppercase">Awaiting Approval</p>
            <p className="text-2xl font-bold text-orange-700 mt-1">{summary.pending_approval_count}</p>
          </div>
          <div className="bg-purple-50 border border-purple-100 rounded-xl p-4">
            <p className="text-xs text-purple-600 font-medium uppercase">External Shares</p>
            <p className="text-2xl font-bold text-purple-700 mt-1">{summary.external_count}</p>
          </div>
          <div className="bg-red-50 border border-red-100 rounded-xl p-4">
            <p className="text-xs text-red-600 font-medium uppercase">Security Alerts</p>
            <p className="text-2xl font-bold text-red-700 mt-1">{summary.alert_count}</p>
          </div>
        </div>
      )}

      {/* Filter bar */}
      <div className="flex items-center gap-3">
        {['all', 'active', 'pending', 'pending_approval', 'rejected', 'revoked', 'expired'].map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              statusFilter === s
                ? 'bg-gray-800 text-white'
                : 'bg-white text-gray-500 border border-gray-200 hover:border-gray-300'
            }`}
          >
            {s === 'pending_approval' ? 'Awaiting Approval' : s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
        <div className="flex-1" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by file, sharer, or recipient..."
          className="w-72 px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-teal-500"
        />
      </div>

      {/* Share table */}
      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading sharing activity...</div>
      ) : shares.length === 0 ? (
        <div className="text-center py-12">
          <div className="mb-3">
            <svg className="w-12 h-12 mx-auto text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
            </svg>
          </div>
          <p className="text-gray-500 font-medium">No shares found</p>
          <p className="text-gray-400 text-sm mt-1">
            {statusFilter !== 'all' ? `No ${statusFilter} shares match your filters` : 'No sharing activity yet'}
          </p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">File</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Shared By</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Shared With</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Sessions</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Last Access</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {shares.map((share) => {
                const badge = STATUS_BADGES[share.status] || STATUS_BADGES.expired;
                const canRevoke = share.status === 'active' || share.status === 'pending';
                return (
                  <tr key={share.share_id} className="border-b border-gray-50 hover:bg-gray-50/50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                        <span className="text-gray-800 font-medium truncate max-w-[200px]" title={share.file_name}>
                          {share.file_name}
                        </span>
                        {share.anomaly_count > 0 && (
                          <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-600">
                            {share.anomaly_count} alert{share.anomaly_count > 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{share.shared_by_name}</td>
                    <td className="px-4 py-3 text-gray-600">{share.shared_with_email}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${badge.bg} ${badge.text}`}>
                        {badge.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{share.session_count}</td>
                    <td className="px-4 py-3 text-gray-400 text-xs">
                      {share.last_access ? formatDateTime(share.last_access) : '--'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {share.status === 'pending_approval' && (
                          <>
                            <button
                              onClick={() => handleApprove(share.share_id)}
                              disabled={approveLoading === share.share_id}
                              className="px-2.5 py-1 text-xs font-medium bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
                            >
                              {approveLoading === share.share_id ? '...' : 'Approve'}
                            </button>
                            <button
                              onClick={() => setRejectingShareId(share.share_id)}
                              className="px-2.5 py-1 text-xs font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50"
                            >
                              Reject
                            </button>
                          </>
                        )}
                        {canRevoke && share.status !== 'pending_approval' && (
                          <button
                            onClick={() => handleRevoke(share.file_id, share.share_id)}
                            disabled={revokeLoading === share.share_id}
                            className="px-3 py-1.5 text-xs font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                          >
                            {revokeLoading === share.share_id ? 'Revoking...' : 'Revoke'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SharingActivityPage;
